import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type Ref } from 'react';
import { pacientes, type PacienteEnLista } from '../api/recursos';
import { Dato } from './comunes';

/**
 * Busca una paciente del expediente y devuelve la elegida.
 *
 * Existe para poder ligar una cita a su expediente, que es el dato que el
 * modelo `Cita` y el contrato admiten desde F2 —`pacienteId`, con su índice—
 * y que ninguna pantalla llegó a mandar nunca. Sin esa liga, una cita y el
 * expediente de la misma persona eran dos cosas sin relación.
 */
export function BuscadorDePaciente({
  valor,
  alElegir,
  etiqueta = 'Paciente del expediente',
  ayuda,
  etiquetaOculta = false,
  referencia,
}: {
  valor: PacienteEnLista | null;
  alElegir(paciente: PacienteEnLista | null): void;
  etiqueta?: string;
  ayuda?: string;
  /** Busqueda de la barra superior: la etiqueta la lee el lector, no se pinta. */
  etiquetaOculta?: boolean;
  /** Para dar el foco con un atajo. */
  referencia?: Ref<HTMLInputElement>;
}) {
  const [texto, setTexto] = useState('');
  const [diferido, setDiferido] = useState('');

  useEffect(() => {
    const temporizador = setTimeout(() => setDiferido(texto), 250);
    return () => clearTimeout(temporizador);
  }, [texto]);

  const consulta = useQuery({
    queryKey: ['pacientes', diferido, 1],
    queryFn: () => pacientes.buscar(diferido, 1),
    placeholderData: keepPreviousData,
  });

  const opciones = consulta.data?.datos ?? [];

  return (
    <Autocomplete
      value={valor}
      onChange={(_evento, elegido) => alElegir(elegido)}
      inputValue={texto}
      onInputChange={(_evento, nuevo) => setTexto(nuevo)}
      options={opciones}
      loading={consulta.isFetching}
      // El servidor ya filtró: volver a filtrar en el navegador escondería
      // resultados que sí coinciden por teléfono o expediente.
      filterOptions={(todas) => todas}
      getOptionLabel={(opcion) => `${opcion.expediente} · ${opcion.nombreCompleto}`}
      isOptionEqualToValue={(opcion, elegida) => opcion.id === elegida.id}
      noOptionsText={diferido ? 'Ninguna paciente con ese dato' : 'Escriba para buscar'}
      renderOption={(props, opcion) => {
        const { key, ...resto } = props as { key: string } & Record<string, unknown>;
        return (
          <Box component="li" key={key} {...resto}>
            <Box sx={{ minWidth: 0, py: '2px' }}>
              <Typography
                sx={{
                  fontSize: 'var(--text-body-dense)',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                }}
              >
                {opcion.nombreCompleto}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                <Dato tono="tenue">{opcion.expediente}</Dato> · {opcion.edad} años
                {opcion.telefono && (
                  <>
                    {' · '}
                    <Dato tono="tenue">{opcion.telefono}</Dato>
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(parametros) => (
        <TextField
          {...parametros}
          inputRef={referencia}
          label={etiquetaOculta ? undefined : etiqueta}
          helperText={ayuda}
          placeholder={
            etiquetaOculta
              ? 'Buscar paciente: nombre, expediente o teléfono'
              : 'Nombre, expediente o teléfono'
          }
          inputProps={{
            ...parametros.inputProps,
            ...(etiquetaOculta ? { 'aria-label': etiqueta } : {}),
          }}
          // Campo de busqueda con lupa a la izquierda. La etiqueta sigue
          // visible salvo en la barra superior; el placeholder solo sugiere.
          InputProps={{
            ...parametros.InputProps,
            startAdornment: (
              <>
                <SearchRoundedIcon
                  aria-hidden
                  sx={{
                    fontSize: 'var(--density-icon)',
                    color: 'var(--text-tertiary)',
                    ml: '4px',
                    mr: '2px',
                    flexShrink: 0,
                  }}
                />
                {parametros.InputProps.startAdornment}
              </>
            ),
            sx: etiquetaOculta ? { bgcolor: 'var(--bg-surface)' } : undefined,
          }}
        />
      )}
    />
  );
}
